/**
 * Data layer for `campaign-states`. Thin typed fetch wrappers over /api/v1/campaign-states.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 *
 * Backend endpoints:
 *   POST /transition           { campaign_id, to_state, reason? } -> { campaign, log }
 *   GET  /history/:campaign_id -> { campaign_id, current_state, history[] }
 *   GET  /allowed/:campaign_id -> { campaign_id, current_state, allowed_next[] }
 */

export type CampaignState =
	| "draft"
	| "inactive"
	| "live"
	| "paused"
	| "archived";

export interface CampaignRow {
	id: string;
	tenant_id: string;
	name: string;
	state: CampaignState;
	created_at: number;
	updated_at: number;
}

export interface CampaignStateLogRow {
	id: string;
	campaign_id: string;
	tenant_id: string;
	from_state: CampaignState | null;
	to_state: CampaignState;
	reason: string | null;
	transitioned_at: number;
}

export interface HistoryResponse {
	campaign_id: string;
	current_state: CampaignState;
	history: CampaignStateLogRow[];
}

export interface AllowedResponse {
	campaign_id: string;
	current_state: CampaignState;
	allowed_next: CampaignState[];
}

export interface TransitionBody {
	campaign_id: string;
	to_state: CampaignState;
	reason?: string;
}

export interface TransitionResponse {
	campaign: CampaignRow;
	log: CampaignStateLogRow;
}

// Pseudo "list" response used by the scaffold-imposed contract — the
// campaign-states feature does not have a list endpoint upstream, so this is
// a stub that always returns an empty list. The real UI never calls this; it
// is kept only so the scaffolded hooks/tests remain type-safe.
export interface ListCampaignStatesResponse {
	count: number;
	items: CampaignRow[];
}

const BASE = "/api/v1/campaign-states";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCampaignStatesResponse> {
	// No upstream list endpoint — return empty shape so existing scaffold
	// hooks/tests continue to type-check.
	return { count: 0, items: [] };
}

export async function getHistory(campaignId: string): Promise<HistoryResponse> {
	const res = await fetch(
		`${BASE}/history/${encodeURIComponent(campaignId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<HistoryResponse>(res);
}

export async function getAllowed(campaignId: string): Promise<AllowedResponse> {
	const res = await fetch(
		`${BASE}/allowed/${encodeURIComponent(campaignId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<AllowedResponse>(res);
}

export async function transition(
	body: TransitionBody,
): Promise<TransitionResponse> {
	const res = await fetch(`${BASE}/transition`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<TransitionResponse>(res);
}

// Legacy stub retained so the scaffolded delete hook still type-checks.
// There is no upstream delete endpoint on campaign-states.
export async function remove(_id: string): Promise<{ removed: boolean }> {
	return { removed: false };
}

// Legacy stub retained so the scaffolded create hook still type-checks.
// There is no upstream create endpoint on campaign-states.
export interface CreateCampaignStatesBody {
	[key: string]: unknown;
}
export async function create(
	_body: CreateCampaignStatesBody,
): Promise<CampaignRow> {
	throw new Error("campaign-states has no create endpoint");
}

// Row alias kept for scaffold compatibility.
export type CampaignStatesRow = CampaignRow;

export const campaignStatesQueryKeys = {
	all: ["campaign-states"] as const,
	list: () => [...campaignStatesQueryKeys.all] as const,
	detail: (id: string) => [...campaignStatesQueryKeys.all, id] as const,
	history: (id: string) =>
		[...campaignStatesQueryKeys.all, id, "history"] as const,
	allowed: (id: string) =>
		[...campaignStatesQueryKeys.all, id, "allowed"] as const,
};
